import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo } from "react";
import "./formBuilderDraggableItem.scss";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useFormBuilderEditorContext } from "../formBuilderEditorContext";
import { formBuilderGenerateDefaultRow } from "../model/formBuilderRow";
import {
  TFormBuilderItem,
  TFormBuilderItemKind,
  formBuilderGenerateDefaultItem,
} from "../model/item/formBuilderItem";
import { IFormBuilderItemMetadata } from "../hooks/itemMetadataMap/useFormBuilderItemMetadataMap";
import { ACCEPTED_FORM_BUILDER_DROP_ITEM } from "../designSpace/views/components/form/FormBuilderFormView";

interface IFormBuilderDraggableItemProps {
  kind: TFormBuilderItemKind;
  metadata: IFormBuilderItemMetadata;
};

export function FormBuilderDraggableItem(props: IFormBuilderDraggableItemProps) {
  const { kind, metadata } = props;

  const { formBuilderController, formBuilderEntityDomNodeRefMap } = useFormBuilderEditorContext();

  const disabled = useMemo(() => {
    if (metadata == null) {
      return true;
    }

    if (!metadata.unique) {
      return false;
    }

    for (const row of formBuilderController.form.children) {
      for (const item of row.children) {
        if (kind.type === item.data.type && kind.role === item.data.role) {
          return true;
        }
      }
    }

    return false;
  }, [metadata, formBuilderController, kind]);

  const [, drag, dragPreview] = useDrag<TFormBuilderItem, unknown, unknown>(() => {
    return {
      type: ACCEPTED_FORM_BUILDER_DROP_ITEM,
      item: formBuilderGenerateDefaultItem(kind),
      canDrag: () => !disabled,
    };
  }, [kind, disabled]);

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  function addFormItemToForm() {
    const newItem = formBuilderGenerateDefaultItem(kind);

    const newRow = formBuilderGenerateDefaultRow([newItem]);

    formBuilderController.addEntity({
      snapshot: {
        type: "row",
        entity: newRow,
        entityIndex: formBuilderController.form.children.length,
      },
    });

    requestAnimationFrame(() => {
      const itemDomNode = formBuilderEntityDomNodeRefMap.current.get(newItem.key);

      if (itemDomNode != null) {
        itemDomNode.scrollIntoView({ behavior: "instant", block: "center" });
      }
    });
  }

  if (metadata == null) {
    return null;
  } else {
    return (
      <button
        ref={drag}
        role="button"
        className="form-builder-draggable-item"
        onClick={addFormItemToForm}
        disabled={disabled}
      >
        <div className="draggable-item-content">
          <FontAwesomeIcon className="draggable-item-icon" icon={metadata.icon} />
          <div className="draggable-item-label">{metadata.label}</div>
        </div>
      </button>
    );
  }
}